import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { User } from 'firebase/auth';
import { 
  loginUser, 
  logoutUser, 
  registerUser, 
  resetPassword,
  getCurrentUser,
  onAuthStateChange,
  UserData as AuthUserData,
  signInWithGoogle,
  resendVerificationEmail,
  updateUserProfile as updateAuthProfile,
  changePassword as changeUserPassword,
  linkGoogleAccount as linkGoogleAccountService
} from '../firebase/authService';
import { getUserProfile, updateUserProfileData } from '../firebase/firestoreService';
import { ALL_SIDEBAR_MENU_ITEMS, UserData, UserPermissions } from '../firebase/usersFirestoreService'; // Import from correct file
import { cache } from '../utils/cacheManager';


export interface AuthContextType {
  currentUser: (User & { role?: string }) | null;
  userProfile: UserData | null;
  loading: boolean;
  isEmailVerified: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string, userData: any) => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  googleSignIn: () => Promise<any>;
  facebookSignIn?: () => Promise<any>;
  resendVerification: () => Promise<any>;
  updateUserProfile: (data: Partial<UserData>) => Promise<any>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<any>;
  linkGoogleAccount: () => Promise<any>;
  isAuthenticated: boolean;
  hasRole: (requiredRole: string) => boolean;
  refreshUserProfile: () => Promise<void>;
    applyUserProfilePatch: (patch: Partial<UserData>) => void;
  // Permission methods
  canViewSection: (section: string) => boolean;
  canPerformAction: (section: string, action: string) => boolean;
  isSuperAdmin: boolean;
  // Additional helper methods
  getCurrentUserPermissions: () => UserPermissions | null;
  // Sidebar visibility helpers
  canViewSidebarItem: (path: string) => boolean;
  getSidebarVisibility: () => Record<string, boolean>;
  isSidebarItemVisible: (path: string) => boolean;
  forceRefreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<(User & { role?: string }) | null>(null);
  const [userProfile, setUserProfile] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

// Create forceRefreshUser function
const forceRefreshUser = useCallback(async () => {
  if (currentUser) {
    console.log('🔁 Force refreshing user profile...');
    // Clear any cached profile data
    cache.invalidate(`user.profile.${currentUser.uid}`);
    
    // Manually fetch fresh profile
    const freshProfile = await fetchUserProfile(currentUser.uid);
    if (freshProfile) {
      setCurrentUser(prev => ({
        ...prev!,
        role: freshProfile.role || 'user'
      }));
    }
  }
}, [currentUser]);

  // Fetch user profile from Firestore
  const fetchUserProfile = async (userId: string): Promise<UserData | null> => {
    try {
      console.log('AuthContext.fetchUserProfile: fetching', userId);
      const profileResp = await getUserProfile(userId);
      if (profileResp.success && profileResp.data) {
        const profile = profileResp.data as UserData;
        console.log('AuthContext.fetchUserProfile: fetched profile', { userId, role: profile.role });
        setUserProfile(profile);
        return profile;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    return null;
  };

  useEffect(() => {
  if (refreshTrigger > 0 && currentUser) {
    fetchUserProfile(currentUser.uid);
  }
}, [refreshTrigger, currentUser]);
  // Refresh user profile
  const refreshUserProfile = async () => {
    console.log('AuthContext.refreshUserProfile: called');
    if (currentUser) {
      await fetchUserProfile(currentUser.uid);
    }
  };

  // Apply a local patch to the userProfile state (optimistic UI)
  const applyUserProfilePatch = (patch: Partial<UserData>) => {
    setUserProfile(prev => {
      if (!prev) return prev;
      // Merge permissions specially to avoid overwriting nested object
      const mergedPermissions = {
        ...(prev.permissions || {}),
        ...(patch.permissions || {})
      } as any;

      return {
        ...prev,
        ...patch,
        permissions: mergedPermissions
      } as UserData;
    });
  };

  


  // Add this helper method to AuthContext.tsx (inside the AuthProvider)

const getSectionPermission = useMemo(() => (section: string, permission: string): boolean => {
  if (!userProfile) return false;
  
  // Super admin can do everything
  if (userProfile.role === 'super_admin') return true;
  
  // Check if user has permissions object
  if (!userProfile.permissions) return false;
  
  // Check specific section permission
  const sectionPermissions = userProfile.permissions[section as keyof UserPermissions];
  if (!sectionPermissions || typeof sectionPermissions !== 'object') return false;
  
  return sectionPermissions[permission as keyof typeof sectionPermissions] || false;
}, [userProfile]);



  // Permission methods
  const canViewSection = useMemo(() => (section: string): boolean => {
    if (!userProfile) return false;
    
    // Super admin can view everything
    if (userProfile.role === 'super_admin') return true;
    
    // Check if user has permissions object
    if (!userProfile.permissions) return false;
    
    // Check specific section permissions
    switch (section) {
      case 'news':
        return userProfile.permissions.news?.view || false;
      case 'library':
        return userProfile.permissions.library?.view || false;
      case 'programs':
        return userProfile.permissions.programs?.view || false;
      case 'accounting':
        return userProfile.permissions.accounting?.view || false;
      case 'users':
        return userProfile.permissions.users?.view || false;
      case 'settings':
        return userProfile.permissions.settings?.view || false;
      default:
        return false;
    }
  }, [userProfile]);

  const canPerformAction = useMemo(() => (section: string, action: string): boolean => {
    if (!userProfile) return false;
    
    // Super admin can do everything
    if (userProfile.role === 'super_admin') return true;
    
    // Check if user has permissions object
    if (!userProfile.permissions) return false;
    
    // Check specific section and action
    const sectionPermissions = userProfile.permissions[section as keyof UserPermissions];
    if (!sectionPermissions || typeof sectionPermissions !== 'object') return false;
    
    return sectionPermissions[action as keyof typeof sectionPermissions] || false;
  }, [userProfile]);

  const isSuperAdmin = useMemo(() => {
    return userProfile?.role === 'super_admin';
  }, [userProfile]);

  const getCurrentUserPermissions = useMemo(() => (): UserPermissions | null => {
    return userProfile?.permissions || null;
  }, [userProfile]);

  const hasRole = (requiredRole: string): boolean => {
    if (!currentUser?.role) return false;
    
    const roleHierarchy = {
      'super_admin': ['super_admin', 'admin', 'moderator', 'user'],
      'admin': ['admin', 'moderator', 'user'],
      'moderator': ['moderator', 'user'],
      'user': ['user']
    };
    
    const userRole = currentUser.role as keyof typeof roleHierarchy;
    return roleHierarchy[userRole]?.includes(requiredRole) || false;
  };

  // Sidebar visibility methods (placed after permission helpers to avoid use-before-declare)
  const canViewSidebarItem = useMemo(() => (path: string): boolean => {
    if (!userProfile) return false;
    
    // Super admin can view everything
    if (userProfile.role === 'super_admin') return true;
    
    // Check custom sidebar visibility first
    if (userProfile.permissions?.sidebarVisibility?.[path] !== undefined) {
      return userProfile.permissions.sidebarVisibility[path];
    }
    
    // Check based on section permissions (backward compatibility)
    const menuItem = ALL_SIDEBAR_MENU_ITEMS.find(item => item.path === path);
    if (!menuItem) return true; // If not in predefined list, show by default
    
    // Check role requirement
    if (menuItem.requiredRole && !hasRole(menuItem.requiredRole)) {
      return false;
    }
    
    // Check section permission
    if (menuItem.requiredSection) {
      return canViewSection(menuItem.requiredSection);
    }
    
    return true; // Default to visible
  }, [userProfile, hasRole, canViewSection]);

  const getSidebarVisibility = useMemo(() => (): Record<string, boolean> => {
    if (!userProfile?.permissions?.sidebarVisibility) return {};
    return userProfile.permissions.sidebarVisibility;
  }, [userProfile]);

  const isSidebarItemVisible = useMemo(() => (path: string): boolean => {
    return canViewSidebarItem(path);
  }, [canViewSidebarItem]);

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        // Set current user with basic info
        const userWithRole = {
          ...user,
          role: 'user' // Default role, will be updated from Firestore
        };
        setCurrentUser(userWithRole);
        setIsEmailVerified(user.emailVerified);

        // Fetch detailed user profile from Firestore
        const profile = await fetchUserProfile(user.uid);
        if (profile) {
          // Update current user with role from Firestore
          setCurrentUser(prev => ({
            ...prev!,
            role: profile.role || 'user'
          }));
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setIsEmailVerified(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await loginUser(email, password);
      if (result.success && result.user) {
        setCurrentUser(result.user);
        setIsEmailVerified(result.user.emailVerified || false);
        
        // Fetch updated profile
        if (result.user.uid) {
          await fetchUserProfile(result.user.uid);
        }
      }
      return result;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, userData: any) => {
    setLoading(true);
    try {
      const result = await registerUser(email, password, userData);
      if (result.success && result.user) {
        setCurrentUser(result.user);
        setIsEmailVerified(false);
        
        // Create initial profile state
        const newProfile: UserData = {
          uid: result.user.uid,
          email: result.user.email!,
          displayName: `${userData.firstName} ${userData.lastName}`,
          role: userData.role || 'user',
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone || '',
          dateOfBirth: userData.dateOfBirth || '',
          gender: userData.gender || 'prefer-not-to-say',
          isActive: true,
          emailVerified: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          lastLoginAt: new Date(),
          termsAccepted: userData.termsAccepted,
          termsAcceptedAt: new Date(),
          marketingEmails: userData.marketingEmails || false,
          provider: ['email'],
          permissions: undefined,
          permissionPreset: undefined
        };
        setUserProfile(newProfile);
      }
      return result;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Registration failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success && result.user) {
        setCurrentUser(result.user);
        setIsEmailVerified(result.user.emailVerified || false);
        
        // Fetch updated profile
        if (result.user.uid) {
          await fetchUserProfile(result.user.uid);
        }
      }
      return result;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Google Sign-In failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await logoutUser();
      setCurrentUser(null);
      setUserProfile(null);
      setIsEmailVerified(false);
    } catch (error: any) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPwd = async (email: string) => {
    try {
      return await resetPassword(email);
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Password reset failed' 
      };
    }
  };

  const resendVerification = async () => {
    if (!currentUser) {
      return { 
        success: false, 
        error: 'No user logged in' 
      };
    }
    
    try {
      const result = await resendVerificationEmail(currentUser);
      if (result.success) {
        setIsEmailVerified(false);
      }
      return result;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Failed to resend verification email' 
      };
    }
  };

  const updateUserProfile = async (data: Partial<UserData>) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    try {
      // Update in Firestore
      await updateUserProfileData(currentUser.uid, {
        ...data,
        updatedAt: new Date()
      });
      
      // Update local state
      setUserProfile(prev => prev ? { ...prev, ...data } : null);
      
      // Update auth profile if needed
      if (data.firstName || data.lastName) {
        const displayName = `${data.firstName || userProfile?.firstName} ${data.lastName || userProfile?.lastName}`;
        await updateAuthProfile(currentUser, { displayName });
      }
      
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Failed to update profile' 
      };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    try {
      return await changeUserPassword(currentUser, currentPassword, newPassword);
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Failed to change password' 
      };
    }
  };

  const linkGoogleAccount = async () => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    try {
      const result = await linkGoogleAccountService(currentUser);
      if (result.success && result.user) {
        setCurrentUser(result.user);
        
        // Update user profile with provider
        await updateUserProfileData(currentUser.uid, {
          provider: Array.from(new Set([...(userProfile?.provider || []), 'google'])),
          updatedAt: new Date()
        });
        
        // Refresh profile
        await fetchUserProfile(currentUser.uid);
      }
      return result;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Failed to link Google account' 
      };
    }
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    isEmailVerified,
    login,
    register,
    logout,
    resetPassword: resetPwd,
    googleSignIn,
    resendVerification,
    updateUserProfile,
    changePassword,
    linkGoogleAccount,
    isAuthenticated: !!currentUser,
    hasRole,
    refreshUserProfile,
    canViewSection,
    canPerformAction,
    isSuperAdmin,
    getCurrentUserPermissions,
    // Sidebar visibility methods
    canViewSidebarItem,
    getSidebarVisibility,
    isSidebarItemVisible,
    applyUserProfilePatch: function (patch: Partial<UserData>): void {
      throw new Error('Function not implemented.');
    },
    forceRefreshUser: function (): Promise<void> {
      throw new Error('Function not implemented.');
    }
  };
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};